import { useEffect, useState } from "react";
import { Mail, Save, Send, Eye, EyeOff, Trash2, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailSettingsPanel = ({ token }) => {
  const [settings, setSettings] = useState({
    sender_name: "",
    sender_email: "",
    reply_to: "",
    live_mode: true,
    api_key_masked: "",
    has_api_key: false,
    effective: null,
  });
  const [newKey, setNewKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/owner/email-settings`, { headers });
      setSettings(r.data);
    } catch (e) {
      toast.error("Failed to load email settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        sender_name: settings.sender_name,
        sender_email: settings.sender_email,
        reply_to: settings.reply_to,
        live_mode: settings.live_mode,
      };
      if (newKey && !newKey.includes("•")) {
        body.api_key = newKey;
      }
      const r = await axios.put(`${API}/owner/email-settings`, body, { headers });
      setSettings(r.data);
      setNewKey("");
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove the stored Resend API key? Sending will fall back to env or simulated mode.")) return;
    try {
      await axios.delete(`${API}/owner/email-settings/api-key`, { headers });
      toast.success("API key cleared");
      load();
    } catch (e) {
      toast.error("Failed to clear");
    }
  };

  const sendTest = async () => {
    if (!testTo) {
      toast.error("Enter a test recipient");
      return;
    }
    setTesting(true);
    try {
      const r = await axios.post(
        `${API}/owner/email-settings/test`,
        { to: testTo, subject: "Test email from DC Tiling Settings" },
        { headers }
      );
      const status = r.data.status;
      if (status === "success") {
        toast.success(`Test sent. Resend ID: ${r.data.email_id || "(none)"}`);
      } else if (status === "simulated") {
        toast.warning(`Simulated only: ${r.data.message}`);
      } else {
        toast.error(`Error: ${r.data.message || "unknown"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="email-settings-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const eff = settings.effective || {};
  const isLive = !!(eff.configured && eff.live_mode);

  return (
    <div className="space-y-6" data-testid="email-settings-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Settings</h2>
          <p className="text-gray-600">
            Configure sender, reply-to, and Resend API key. Saved values override environment variables.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-email-settings-btn">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      {/* Status banner */}
      <div
        className={`rounded-xl p-4 flex items-start gap-3 border ${
          isLive ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
        }`}
        data-testid="email-status-banner"
      >
        {isLive ? (
          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        )}
        <div className="text-sm">
          <p className={`font-semibold ${isLive ? "text-green-900" : "text-amber-900"}`}>
            {isLive ? "Live — emails will be sent via Resend" : "Disabled — emails will be simulated only"}
          </p>
          <p className="text-gray-700 mt-1">
            Source: <span className="font-mono">{eff.source || "none"}</span> · Resend SDK:{" "}
            {eff.resend_sdk_installed ? "installed" : "missing"} · Live mode: {eff.live_mode ? "ON" : "OFF"}
          </p>
          {eff.sender_email && (
            <p className="text-gray-700">
              Effective sender: <span className="font-mono">{eff.sender_name ? `${eff.sender_name} <${eff.sender_email}>` : eff.sender_email}</span>
            </p>
          )}
          {eff.reply_to && (
            <p className="text-gray-700">
              Reply-To: <span className="font-mono">{eff.reply_to}</span>
            </p>
          )}
        </div>
      </div>

      {/* Sender + Reply-To */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="font-semibold">Sender</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sender_name">Sender Name</Label>
            <Input
              id="sender_name"
              placeholder="e.g., DC Tiling & Bathrooms"
              value={settings.sender_name || ""}
              onChange={(e) => setSettings((s) => ({ ...s, sender_name: e.target.value }))}
              data-testid="sender-name-input"
            />
          </div>
          <div>
            <Label htmlFor="sender_email">Sender Email</Label>
            <Input
              id="sender_email"
              type="email"
              placeholder="hello@dctiling.co.uk"
              value={settings.sender_email || ""}
              onChange={(e) => setSettings((s) => ({ ...s, sender_email: e.target.value }))}
              data-testid="sender-email-input"
            />
            <p className="text-xs text-gray-500 mt-1">Domain must be verified in Resend.</p>
          </div>
          <div>
            <Label htmlFor="reply_to">Reply-To Email</Label>
            <Input
              id="reply_to"
              type="email"
              placeholder="danny@dctiling.co.uk"
              value={settings.reply_to || ""}
              onChange={(e) => setSettings((s) => ({ ...s, reply_to: e.target.value }))}
              data-testid="reply-to-input"
            />
            <p className="text-xs text-gray-500 mt-1">Customer replies route here.</p>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
            <div>
              <Label htmlFor="live_mode">Live Mode</Label>
              <p className="text-xs text-gray-500">Off = simulate only (no real send)</p>
            </div>
            <Switch
              id="live_mode"
              checked={!!settings.live_mode}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, live_mode: v }))}
              data-testid="live-mode-switch"
            />
          </div>
        </div>
      </div>

      {/* API Key */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 space-y-4">
        <h3 className="font-semibold">Resend API Key</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <Label htmlFor="api_key">
              {settings.has_api_key ? "Current key (masked)" : "Add API key"}
            </Label>
            <div className="relative">
              <Input
                id="api_key"
                type={showKey ? "text" : "password"}
                placeholder={settings.has_api_key ? settings.api_key_masked : "re_xxxxxxxxxxxx"}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                data-testid="api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label="Toggle visibility"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Paste a new key to replace. Leave blank to keep the existing one.
            </p>
          </div>
          {settings.has_api_key && (
            <Button variant="outline" onClick={clearKey} data-testid="clear-key-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Key
            </Button>
          )}
        </div>
      </div>

      {/* Test send */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 space-y-4">
        <h3 className="font-semibold">Send Test Email</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <Label htmlFor="test_to">Recipient</Label>
            <Input
              id="test_to"
              type="email"
              placeholder="you@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              data-testid="test-recipient-input"
            />
          </div>
          <Button onClick={sendTest} disabled={testing} className="bg-primary hover:bg-primary/90" data-testid="send-test-btn">
            {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Test
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailSettingsPanel;
